/* eslint-disable sort-keys-fix/sort-keys-fix -- CSS-in-JS pseudo-selector keys use semantic ordering */
// Backstage UserListPicker (Owned, Starred, All) styled to the NS design.
// The upstream component stays; everything goes through its makeStyles name
// `CatalogReactUserListPicker`, which survives a minified prod build.
// Order and icons hook into upstream's data-testids, pinned by the test file.

import { glyphMask } from '../../icons/glyph-mask';
import { iconEye } from '../../icons/glyphs/eye';
import { iconStar } from '../../icons/glyphs/star';
import { iconUser } from '../../icons/glyphs/user';
import type { IconGlyph } from '../../icons/icon';

const ALL = '[data-testid="user-picker-all"]';

const glyph = (icon: IconGlyph) => ({
  content: '""',
  width: 24,
  height: 24,
  flexShrink: 0,
  backgroundColor: 'currentColor',
  maskImage: glyphMask(icon),
  maskSize: '100% 100%',
});

// A selected option shows the Tractie -thick variant, as the nav does.
const selectedGlyph = (icon: IconGlyph) => ({
  maskImage: glyphMask(icon.selected ?? icon),
});

export const CatalogReactUserListPickerLightOverrides = {
  root: {
    display: 'flex',
    flexDirection: 'column' as const,
    padding: 16,
    backgroundColor: 'var(--ns-color-bg-default)',
    border: '1px solid var(--ns-color-border-default)',
    borderRadius: 'var(--ns-radius-l)',
    boxShadow: 'none',
    '& $title:first-child': { paddingTop: 0 },
    // The design puts the org group (All) first, upstream renders it last.
    // Only swap where reading-flow makes Tab follow the visual order (Chrome
    // and Edge 137+). Other browsers keep the DOM order, Personal first, so
    // what you see and where Tab goes never disagree.
    '@supports (reading-flow: flex-visual)': {
      readingFlow: 'flex-visual',
      [`& $title:first-child:not(:has(+ $groupWrapper ${ALL}))`]: {
        paddingTop: 16,
      },
      [`& $title:has(+ $groupWrapper ${ALL})`]: { order: -2, paddingTop: 0 },
      [`& $groupWrapper:has(${ALL})`]: { order: -1 },
    },
  },
  title: {
    margin: 0,
    padding: '16px 0 8px',
    textTransform: 'none' as const,
    fontSize: 14,
    fontWeight: 400,
    lineHeight: 1.5,
    color: 'var(--ns-color-fg-default)',
  },
  // The rows sit directly in the tile: no border or background of their own.
  // Visible overflow lets a row state reach 8px past the content edge.
  groupWrapper: {
    margin: 0,
    overflow: 'visible',
    backgroundColor: 'transparent',
    border: 'none',
    borderRadius: 0,
    boxShadow: 'none',
    // The count is a sibling of the MenuItem, not a child. Let the pointer
    // fall through, so hover and click work on the whole row.
    '& .MuiListItemSecondaryAction-root': {
      pointerEvents: 'none' as const,
      right: 0,
      fontSize: 14,
      lineHeight: 1.5,
      color: 'var(--ns-color-fg-default)',
    },
    // A count-0 option is disabled upstream, its count dims along with it.
    '& .Mui-disabled + .MuiListItemSecondaryAction-root': {
      color: 'var(--ns-color-fg-disabled)',
    },
  },
  // The negative margin lets the row state reach 8px past the content edge,
  // the padding keeps icon and label on that edge.
  menuItem: {
    minHeight: 40,
    margin: '0 -8px',
    padding: '8px 48px 8px 8px',
    gap: 8,
    borderRadius: 'var(--ns-radius-m)',
    color: 'var(--ns-color-fg-emphasis)',
    // Upstream's divider keeps its 1px of space, but the design shows no line.
    '&.MuiListItem-divider': { borderBottomColor: 'transparent' },
    '& .MuiListItemText-root': { margin: 0 },
    '& .MuiTypography-body1': {
      fontSize: 16,
      lineHeight: 1.5,
      color: 'inherit',
    },
    // Order matters at equal specificity: focus first, so hover and pressed
    // show on a focused item; selected last, so it keeps its own colours.
    '&.Mui-focusVisible': {
      backgroundColor: 'transparent',
      outline: '2px solid var(--ns-color-border-focus)',
      outlineOffset: -2,
    },
    '&:hover': { backgroundColor: 'var(--ns-color-bg-hover)' },
    '&:active': { backgroundColor: 'var(--ns-color-bg-pressed)' },
    '&.Mui-selected': {
      backgroundColor: 'var(--ns-color-bg-selected-default)',
      color: 'var(--ns-color-fg-selected)',
    },
    '&.Mui-selected:hover': {
      backgroundColor: 'var(--ns-color-bg-selected-hover)',
    },
    '&.Mui-selected:active': {
      backgroundColor: 'var(--ns-color-bg-selected-pressed)',
    },
    // A touch screen keeps :hover after a tap. JSS emits this block after
    // every rule above, so it skips :active to keep the pressed state.
    '@media (hover: none)': {
      '&:hover:not(:active)': { backgroundColor: 'transparent' },
      '&.Mui-selected:hover:not(:active)': {
        backgroundColor: 'var(--ns-color-bg-selected-default)',
      },
    },
    '&.Mui-selected .MuiTypography-body1': { fontWeight: 700 },
    // Upstream disables a count-0 option. It dims with the NS disabled token
    // instead of MUI's opacity, which would stack on top of it. The icon
    // follows through currentColor.
    '&.Mui-disabled': {
      opacity: 1,
      color: 'var(--ns-color-fg-disabled)',
    },
    [`&${ALL}::before`]: glyph(iconEye),
    '&[data-testid="user-picker-owned"] $listIcon::before': glyph(iconUser),
    '&[data-testid="user-picker-starred"] $listIcon::before': glyph(iconStar),
    [`&.Mui-selected${ALL}::before`]: selectedGlyph(iconEye),
    '&.Mui-selected[data-testid="user-picker-owned"] $listIcon::before':
      selectedGlyph(iconUser),
    '&.Mui-selected[data-testid="user-picker-starred"] $listIcon::before':
      selectedGlyph(iconStar),
  },
  listIcon: {
    minWidth: 0,
    color: 'inherit',
    '& svg': { display: 'none' },
  },
} as const;

// Dark variant: every colour token is mode-aware via tokens.css.
export const CatalogReactUserListPickerDarkOverrides = {
  ...CatalogReactUserListPickerLightOverrides,
} as const;
